// src/api/ai.js
import { encryptData } from '../helpers/encryptDecrypt';
import { API_URL } from './api';
import instance from "./api";
const API_BASE_URL = API_URL+'/daiko-ai';

// These will now be passed as parameters to the functions.

export const sendQueryToDaikoAI = async (query, token, organizationId, branchId, userContext = null) => {
  if (!token || !organizationId) {
    console.error("sendQueryToDaikoAI: Missing token or organizationId");
    throw new Error("Missing required parameters: token or organization ID.");
  }
  const requestBody = {
    query,
    org_id: organizationId,
    branch_id: branchId, // branchId can be null or undefined if not applicable
  };

  if (userContext) {
    requestBody.user_context = userContext;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`, // Use dynamic token
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      // Attempt to parse error response, but provide a fallback
      let errorText = await response.text(); // Get raw text first
      let errorData;
      try {
        errorData = JSON.parse(errorText); // Try to parse it
      } catch (e) {
        // If response is not JSON or other parsing error
        console.error("Failed to parse error response from AI API as JSON. Raw response text:", errorText);
        throw new Error(`API request failed with status ${response.status}. Response was not valid JSON.`);
      }
      console.error('Daiko AI API Error (parsed from JSON):', errorData);
      throw new Error(errorData.message || `API request failed with status ${response.status}`);
    }

    // If response.ok is true, still attempt to parse as JSON but handle potential HTML responses
    const responseText = await response.text();
    try {
      return JSON.parse(responseText);
    } catch (e) {
      console.error('Successfully fetched from AI API, but response was not valid JSON. Raw response text:', responseText);
      throw new Error('AI service returned a non-JSON response. Check console for details.');
    }

  } catch (error) {
    console.error('Error sending query to Daiko AI:', error);
    // Add a more user-friendly error message or rethrow for component-level handling
    throw new Error(error.message || 'Failed to communicate with AI service.');
  }
};

export const executeDaikoAISQL = async (sqlQuery, chatId, token, organizationId, branchId) => {
  if (!token || !organizationId) {
    console.error("executeDaikoAISQL: Missing token or organizationId");
    throw new Error("Missing required parameters: token or organization ID.");
  }
  const requestBody = {
    sql: sqlQuery,      // Assuming backend expects 'sql' based on your cursor position in previous turn
    chat_id: chatId,
    organization_id: organizationId,
    branch_id: branchId, // branchId can be null or undefined
  };
  // console.log("Executing SQL with body:", requestBody);

  try {
    const response = await fetch(`${API_BASE_URL}/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`, // Use dynamic token
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        console.error("Failed to parse error response from AI /execute API as JSON. Raw response text:", errorText);
        throw new Error(`AI /execute API request failed with status ${response.status}. Response was not valid JSON.`);
      }
      console.error('Daiko AI /execute API Error (parsed from JSON):', errorData);
      throw new Error(errorData.message || `AI /execute API request failed with status ${response.status}`);
    }

    const responseText = await response.text();
    try {
      return JSON.parse(responseText);
    } catch (e) {
      console.error('Successfully fetched from AI /execute API, but response was not valid JSON. Raw response text:', responseText);
      throw new Error('AI /execute service returned a non-JSON response. Check console for details.');
    }
  } catch (error) {
    console.error('Error executing SQL query via Daiko AI /execute:', error);
    throw new Error(error.message || 'Failed to execute SQL query via AI service.');
  }
}; 

export const getAIMessageCount = async (orgId) => {
  const oId = encryptData(orgId);
  return await instance.get(`${API_BASE_URL}/message-count/${oId}`);
}